import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import AuthShell, { AuthField, EuFundingNote } from "../components/auth/AuthShell";
import PasswordMeter from "../components/PasswordMeter";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { usePageTitle } from "../hooks/usePageTitle";
import { GameIcon } from "../components/game";
import { athleteAges, MAX_CHILD_AGE, MIN_ATHLETE_AGE } from "../lib/athleteAge";
import {
  validateParentForm,
  validateAthleteForm,
  parentPayload,
  athletePayload,
  ADULT_AGE,
  MAX_SELF_AGE,
  type ParentForm,
  type AthleteForm,
} from "../lib/registerFree";

/**
 * Înregistrare din aplicație, fără invitație.
 *
 * Until now the only way into the app was an invitation from a club: the login
 * screen hid every registration link when running natively and the routes
 * redirected back to it, so a parent who installed the app from the store had
 * nowhere to go. The flows existed on the backend — they were just reachable
 * only from the presentation site, server-to-server behind a shared secret.
 *
 * Free plan only, deliberately. A paid subscription bought inside the app is
 * the kind of thing Apple expects to see going through In-App Purchase, and
 * upgrading later from the web avoids that argument entirely. It also keeps
 * this screen short, which matters more than it sounds for a form somebody
 * fills in on a phone.
 *
 * No club is chosen here. The account stands on its own and the club arrives
 * afterwards, by invitation or join link — the same shape the website's
 * registration has always had.
 */

type Who = "parent" | "athlete" | null;

const emptyParent: ParentForm = {
  parent_name: "",
  parent_email: "",
  parent_phone: "",
  password: "",
  child_first_name: "",
  child_age: "",
  child_email: "",
  child_username: "",
  child_password: "",
  terms: false,
};

const emptyAthlete: AthleteForm = {
  first_name: "",
  age: "",
  email: "",
  username: "",
  password: "",
  guardian_name: "",
  guardian_email: "",
  guardian_phone: "",
  guardian_consent: false,
  terms: false,
};

const Choice = ({
  title,
  subtitle,
  icon,
  onClick,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-start w-full gap-3 p-4 text-left transition border bg-slate-50 border-slate-200 rounded-2xl hover:border-indigo-400 hover:bg-indigo-50/60 active:scale-[0.99]"
  >
    <span className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-white bg-indigo-500 rounded-xl">
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block font-bold text-slate-800">{title}</span>
      <span className="block mt-0.5 text-xs leading-relaxed text-slate-500">{subtitle}</span>
    </span>
  </button>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs leading-relaxed text-slate-500">{children}</p>
);

export default function RegisterFree() {
  const { t } = useTranslation();
  const nav = useNavigate();
  usePageTitle(t("auth.register_title", "Cont nou"));

  const [who, setWho] = useState<Who>(null);
  const [parent, setParent] = useState<ParentForm>(emptyParent);
  const [athlete, setAthlete] = useState<AthleteForm>(emptyAthlete);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<{ title: string; body: string; username?: string | null } | null>(null);

  const setP = (k: keyof ParentForm, v: any) => setParent((f) => ({ ...f, [k]: v }));
  const setA = (k: keyof AthleteForm, v: any) => setAthlete((f) => ({ ...f, [k]: v }));

  async function submitParent(e: React.FormEvent) {
    e.preventDefault();
    const problem = validateParentForm(parent);
    if (problem) return setErr(problem);
    setErr(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-parent", parentPayload(parent));
      setDone({
        title: "Conturile au fost create",
        body: "Ți-am trimis un email de confirmare. Apasă linkul din el și se activează amândouă conturile — al tău și al copilului. Verifică și folderul Spam.",
        username: data?.child?.username || null,
      });
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Nu am putut crea contul. Încearcă din nou.");
    }
    setLoading(false);
  }

  async function submitAthlete(e: React.FormEvent) {
    e.preventDefault();
    const problem = validateAthleteForm(athlete);
    if (problem) return setErr(problem);
    setErr(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-athlete", athletePayload(athlete));
      // An athlete who signed up with a username and no email has nothing to
      // confirm — there is no mailbox — and the server activates them straight
      // away. Telling them to check an inbox they never gave us would leave
      // them waiting for an email that is never coming.
      const hasEmail = !!athlete.email.trim();
      setDone({
        title: "Contul a fost creat",
        body: hasEmail
          ? "Ți-am trimis un email de confirmare. Apasă linkul din el ca să îți activezi contul. Verifică și folderul Spam."
          : "Te poți autentifica acum cu username-ul și parola alese.",
        username: data?.athlete?.username || null,
      });
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Nu am putut crea contul. Încearcă din nou.");
    }
    setLoading(false);
  }

  const submitButton = (label: string) => (
    <button
      type="submit"
      disabled={loading}
      className="w-full py-3 font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-60 active:scale-[0.99] transition"
    >
      {loading ? t("common.loading", "Se trimite...") : label}
    </button>
  );

  const termsBox = (checked: boolean, onChange: (v: boolean) => void) => (
    <label className="flex items-start gap-2 text-xs text-slate-600">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span>
        Am citit și accept{" "}
        <Link to="/terms" className="font-semibold text-indigo-600 underline">
          Termenii și Condițiile
        </Link>{" "}
        și{" "}
        <Link to="/privacy" className="font-semibold text-indigo-600 underline">
          Politica de confidențialitate
        </Link>
        .
      </span>
    </label>
  );

  // ---------------------------------------------------------------- done --

  if (done) {
    return (
      <AuthShell
        icon={<GameIcon name="trophy" className="w-8 h-8" />}
        title={done.title}
        subtitle="Mai e un pas"
        topRight={<LanguageSwitcher />}
        footer={<EuFundingNote light />}
      >
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-slate-700">{done.body}</p>

          {done.username && (
            <div className="p-3 border rounded-xl border-indigo-200 bg-indigo-50">
              <p className="text-xs text-indigo-900">
                Username pentru autentificare:{" "}
                <span className="font-mono font-bold">{done.username}</span>
              </p>
              <p className="mt-1 text-xs text-indigo-800">
                Notează-l — cu el se intră în aplicație, nu cu o adresă de email.
              </p>
            </div>
          )}

          <button
            onClick={() => nav("/login")}
            className="w-full py-3 font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700"
          >
            Mergi la autentificare
          </button>
        </div>
      </AuthShell>
    );
  }

  // --------------------------------------------------------------- forms --

  return (
    <AuthShell
      icon={<GameIcon name="trophy" className="w-8 h-8" />}
      title={who === null ? "Creează-ți cont" : who === "parent" ? "Cont de părinte" : "Cont de sportiv"}
      subtitle={
        who === null
          ? "Alege cum folosești aplicația"
          : who === "parent"
            ? "Contul tău și al copilului, dintr-un pas"
            : "Pentru sportivi care se înscriu singuri"
      }
      topRight={<LanguageSwitcher />}
      footer={<EuFundingNote light />}
    >
      {err && (
        <div className="p-3 mb-4 text-sm border text-rose-700 bg-rose-50 border-rose-200 rounded-xl">{err}</div>
      )}

      {who === null && (
        <div className="space-y-3">
          <Choice
            title="Sunt părinte"
            subtitle="Îți faci contul tău și contul copilului. Copilul poate intra cu email sau, dacă nu are, cu un username."
            icon={<GameIcon name="heart" className="w-5 h-5" />}
            onClick={() => setWho("parent")}
          />
          <Choice
            title="Sunt sportiv"
            subtitle="Îți faci singur cont. Dacă ai sub 18 ani, îți trebuie numele, emailul și acordul unui părinte."
            icon={<GameIcon name="medal" className="w-5 h-5" />}
            onClick={() => setWho("athlete")}
          />

          <Note>
            Contul se creează pe planul gratuit. Clubul se adaugă ulterior, când primești o invitație
            sau un link de înscriere de la antrenor.
          </Note>

          <p className="pt-2 text-sm text-center text-slate-600">
            Ai deja cont?{" "}
            <Link to="/login" className="font-semibold text-indigo-600">
              Autentifică-te
            </Link>
          </p>
        </div>
      )}

      {who === "parent" && (
        <form onSubmit={submitParent} className="space-y-4">
          <div className="space-y-3">
            <p className="text-xs font-bold tracking-wide uppercase text-slate-400">Despre tine</p>
            <AuthField
              label="Numele tău"
              value={parent.parent_name}
              onChange={(e) => setP("parent_name", e.target.value)}
              autoComplete="name"
              required
            />
            <AuthField
              label="Emailul tău"
              type="email"
              value={parent.parent_email}
              onChange={(e) => setP("parent_email", e.target.value)}
              autoComplete="email"
              required
            />
            <AuthField
              label="Telefon (opțional)"
              type="tel"
              value={parent.parent_phone}
              onChange={(e) => setP("parent_phone", e.target.value)}
              autoComplete="tel"
            />
            <div>
              <AuthField
                label="Parola ta"
                type="password"
                value={parent.password}
                onChange={(e) => setP("password", e.target.value)}
                autoComplete="new-password"
                required
              />
              <PasswordMeter value={parent.password} />
            </div>
          </div>

          <div className="pt-2 space-y-3 border-t border-slate-100">
            <p className="pt-2 text-xs font-bold tracking-wide uppercase text-slate-400">Despre copil</p>
            <AuthField
              label="Numele copilului"
              value={parent.child_first_name}
              onChange={(e) => setP("child_first_name", e.target.value)}
              required
            />
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1.5">Vârsta copilului</span>
              <select
                value={parent.child_age}
                onChange={(e) => setP("child_age", e.target.value)}
                required
                className="w-full px-4 py-3 border bg-slate-50 border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Alege...</option>
                {athleteAges(MAX_CHILD_AGE).map((a) => (
                  <option key={a} value={a}>
                    {a} ani
                  </option>
                ))}
              </select>
            </label>

            <AuthField
              label="Emailul copilului (dacă are)"
              type="email"
              value={parent.child_email}
              onChange={(e) => setP("child_email", e.target.value)}
              autoComplete="off"
            />
            <AuthField
              label="Sau username pentru copil"
              value={parent.child_username}
              onChange={(e) => setP("child_username", e.target.value)}
              placeholder="ex: andrei.pop"
              autoComplete="off"
            />
            <Note>
              Copilul are nevoie de unul din cele două ca să intre în aplicație. Dacă nu are email
              propriu, alege-i un username: litere mici, cifre, punct sau underscore.
            </Note>

            <div>
              <AuthField
                label="Parola copilului"
                type="password"
                value={parent.child_password}
                onChange={(e) => setP("child_password", e.target.value)}
                autoComplete="new-password"
                required
              />
              <PasswordMeter value={parent.child_password} />
            </div>
          </div>

          {termsBox(parent.terms, (v) => setP("terms", v))}
          {submitButton("Creează conturile")}
          <button
            type="button"
            onClick={() => {
              setWho(null);
              setErr(null);
            }}
            className="w-full text-sm font-semibold text-slate-500"
          >
            Înapoi
          </button>
        </form>
      )}

      {who === "athlete" && (
        <form onSubmit={submitAthlete} className="space-y-4">
          <AuthField
            label="Numele tău"
            value={athlete.first_name}
            onChange={(e) => setA("first_name", e.target.value)}
            autoComplete="name"
            required
          />
          {/* A number box, not a picker. The age lists in this app stop at 18
              because they are for children; a senior who registers themselves
              can be any age, and the server accepts up to 100. */}
          <AuthField
            label="Vârsta"
            type="number"
            inputMode="numeric"
            min={MIN_ATHLETE_AGE}
            max={MAX_SELF_AGE}
            value={athlete.age}
            onChange={(e) => setA("age", e.target.value)}
            placeholder="ex: 16"
            required
          />

          <AuthField
            label="Emailul tău (dacă ai)"
            type="email"
            value={athlete.email}
            onChange={(e) => setA("email", e.target.value)}
            autoComplete="email"
          />
          <AuthField
            label="Sau username"
            value={athlete.username}
            onChange={(e) => setA("username", e.target.value)}
            placeholder="ex: andrei.pop"
            autoComplete="off"
          />
          <Note>Ai nevoie de unul din cele două ca să te poți autentifica.</Note>

          <div>
            <AuthField
              label="Parola"
              type="password"
              value={athlete.password}
              onChange={(e) => setA("password", e.target.value)}
              autoComplete="new-password"
              required
            />
            <PasswordMeter value={athlete.password} />
          </div>

          {/* Shown the moment the age says minor, not after a failed submit:
              the server refuses without these and the person should see why
              before they have typed everything else. */}
          {!!athlete.age && Number(athlete.age) < ADULT_AGE && (
            <div className="p-3 space-y-3 border rounded-xl border-amber-200 bg-amber-50">
              <p className="text-xs font-semibold text-amber-900">
                Ai sub 18 ani, deci e nevoie de acordul unui părinte sau tutore.
              </p>
              <AuthField
                label="Numele părintelui/tutorelui"
                value={athlete.guardian_name}
                onChange={(e) => setA("guardian_name", e.target.value)}
                required
              />
              <AuthField
                label="Emailul părintelui/tutorelui"
                type="email"
                value={athlete.guardian_email}
                onChange={(e) => setA("guardian_email", e.target.value)}
                required
              />
              <AuthField
                label="Telefonul părintelui (opțional)"
                type="tel"
                value={athlete.guardian_phone}
                onChange={(e) => setA("guardian_phone", e.target.value)}
              />
              <label className="flex items-start gap-2 text-xs text-amber-900">
                <input
                  type="checkbox"
                  checked={athlete.guardian_consent}
                  onChange={(e) => setA("guardian_consent", e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-amber-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>
                  Părintele/tutorele a citit informațiile și își dă acordul pentru acest cont.
                </span>
              </label>
              <p className="text-xs text-amber-800">
                Dacă adresa părintelui are deja cont în aplicație, contul tău se leagă automat de al
                lui.
              </p>
            </div>
          )}

          {termsBox(athlete.terms, (v) => setA("terms", v))}
          {submitButton("Creează contul")}
          <button
            type="button"
            onClick={() => {
              setWho(null);
              setErr(null);
            }}
            className="w-full text-sm font-semibold text-slate-500"
          >
            Înapoi
          </button>
        </form>
      )}
    </AuthShell>
  );
}
